import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { type Persona, type FeatureCardData, personaContent } from '@/lib/persona-content';

interface FeatureCardProps {
  data: FeatureCardData;
}

const FeatureCard = ({ data }: FeatureCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = data.icon;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={cn(
        "group bg-card rounded-2xl p-8 border border-border/50 transition-all duration-300",
        data.hoverBorderColor,
        data.hoverShadowColor
      )}>
        {/* Icon */}
        <div className={cn(
          "inline-flex p-3 rounded-xl bg-gradient-to-br mb-4",
          data.iconGradientFrom,
          data.iconGradientTo
        )}>
          <Icon className={cn("w-7 h-7", data.iconColor)} />
        </div>

        {/* Tag */}
        <span className={cn(
          "inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-4",
          data.tagColor
        )}>
          {data.tag}
        </span>

        {/* Heading */}
        <h3 className="text-2xl font-display font-bold mb-4">
          {data.heading}
        </h3>

        {/* First Paragraph - Always visible */}
        <div className="text-muted-foreground leading-relaxed">
          <p>{data.firstParagraph}</p>
        </div>

        {/* Collapsible Content */}
        <CollapsibleContent className="mt-4">
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            {data.expandedContent.map((text, i) => (
              <p key={i} className="text-sm">{text}</p>
            ))}
          </div>
        </CollapsibleContent>

        {/* Toggle Button */}
        <CollapsibleTrigger asChild>
          <button className="mt-4 flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
            {isOpen ? 'Show Less' : 'Read More'}
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform duration-200",
              isOpen && "rotate-180"
            )} />
          </button>
        </CollapsibleTrigger>
      </div>
    </Collapsible>
  );
};

interface FeaturesShowcaseProps {
  persona?: Persona;
}

const FeaturesShowcase = ({ persona = 'individual' }: FeaturesShowcaseProps) => {
  const features = personaContent[persona].features;

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background to-[hsl(220,25%,8%)]">
      <div className="container">
        {/* Section Header */}
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16">
          Everything You Need. <span className="gradient-text">Nothing You Don't.</span>
        </h2>

        {/* Feature Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {features.map((feature) => (
            <FeatureCard key={feature.heading} data={feature} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;
